import { defineStore } from 'pinia'
import * as contractApi from '../services/api/contract'

export interface IContractData {
  id: number
  status: string
  scheduled_at: string
  note: string
  diagnostic: any
  files: any
  checking_car_at_sr: boolean
  car_info_sale_source: string
  sale_source: string
  referral_source: string
  radius_range: string
  requested_price: number
  purchase_price: number
  selling_price: number
  commission: number
  receiver: any
  pay_withdrawal_fee: string
  pay_cc_fee: string
  customer_id: number
  car_id: number
  request_id: number
  file_user: string
  contract_user: string
  assignee_id: number
  car_info_user_id: number
  car_info_user_name: string
  created_at: string
  car: any
  customer: any
  purchasing_admin?: any
  purchasing_supervisor?: any
  director?: any
}

export const useContractStore = defineStore('contract', {
  state: () => ({
    contract: {
      id: 0,
      status: '',
      scheduled_at: '',
      note: '',
      diagnostic: {
        chassis: true,
        engine: true,
        car_body: true,
        mortgage_agreement: true,
        options: true,
        keys: true,
        accessories: true,
        note: '',
      },
      files: {
        legal_certificate: 'personal',
        registry_certificate: true,
        autonomous_file: true,
        authorized_contract: true,
        sale_contract: true,
        vat_invoice: true,
        bhvc: true,
        btdb: true,
        note: '',
      },
      checking_car_at_sr: true,
      car_info_sale_source: '',
      sale_source: '',
      referral_source: '',
      radius_range: '',
      requested_price: 0,
      purchase_price: 0,
      selling_price: 0,
      commission: 0,
      receiver: {
        bank_info: '',
        phone: '',
        bank_name: '',
        bank_no: '',
      },
      pay_withdrawal_fee: '',
      pay_cc_fee: '',
      customer_id: 0,
      car_id: 0,
      request_id: 0,
      assignee_id: 0,
      file_user: '',
      contract_user: '',
      car_info_user_id: 0,
      car_info_user_name: '',
      created_at: '',
      car: {},
      customer: {},
    } as IContractData,
    isLoading: false,
  }),

  actions: {
    setContract(data: IContractData) {
      this.contract = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getContract(contract_id: number) {
      try {
        const { data } = await contractApi.get(contract_id)
        this.setContract(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async updateContract(payload: any) {
      try {
        const { data } = await contractApi.update(this.contract.id, payload)
        this.setContract(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async rejectContract(payload: any) {
      try {
        const { data } = await contractApi.reject(this.contract.id, payload)
        this.setContract(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async remakeContract(payload: any) {
      try {
        const { data } = await contractApi.remake(this.contract.id, payload)
        this.setContract(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async approveContract() {
      try {
        const { data } = await contractApi.approve(this.contract.id)
        this.setContract(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },
  },
})

export const useContractListStore = defineStore('contractList', {
  state: () => ({
    contracts: [] as IContractData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setContracts(data: IContractData[]) {
      this.contracts = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countContracts() {
      try {
        const { data } = await contractApi.count()
        this.setCount(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async listContracts(params: any) {
      try {
        this.setLoading(true)
        const { data, total } = (await contractApi.list(params)) as any
        this.setContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return { data, total }
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listContractsByCustomer(params: any) {
      try {
        this.setLoading(true)
        const { data, total } = (await contractApi.listByCustomer(params)) as any
        this.setContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return { data, total }
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllContractsByDirector(params: any) {
      try {
        this.setLoading(true)
        const { data, total } = (await contractApi.listAllByDirector(params)) as any
        this.setContracts(data)
        this.setTotal(total)
        this.setLoading(false)
        return { data, total }
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
